// Post-auth redirect helpers.
//
// The portal is often reached from sibling sites (landing page) that want the
// user returned to themselves after login. To support that we accept an
// absolute URL in `?from=`, but only if its host matches the configured
// COOKIE_DOMAIN — otherwise a tampered `?from=` could be used as an open
// redirect to a phishing page.

const COOKIE_DOMAIN = (process.env.REACT_APP_COOKIE_DOMAIN || '').replace(/^\.+/, '');

// Client 2026-08-13: fall back to the app's own registrable domain (eTLD+1)
// when REACT_APP_COOKIE_DOMAIN wasn't baked in at build time. Prevents the
// silent "landing → login → dashboard" bug that used to hit whenever the
// env var pointed at the wrong host or was missing entirely.
//
// This is safe because the host we derive from is the one the browser is
// already loading the app from — any URL we allow through has to have
// been signed off by DNS + TLS for that same registrable domain.
function derivedParentDomain() {
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname;
  if (!host || host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return '';
  const parts = host.split('.');
  if (parts.length <= 2) return host;                    // e.g. mydrai.co
  return parts.slice(-2).join('.');                       // e.g. app.mydrai.co → mydrai.co
}

// True if `raw` is an http/https URL whose host is COOKIE_DOMAIN or a
// subdomain of it. Root-relative paths return false — callers handle those
// with React Router's in-app navigate.
export function isTrustedAbsoluteUrl(raw) {
  if (!raw || typeof raw !== 'string') return false;
  if (raw.startsWith('/') && !raw.startsWith('//')) return false;
  try {
    const u = new URL(raw);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
    const domain = COOKIE_DOMAIN || derivedParentDomain();
    if (!domain) return false;
    return u.hostname === domain || u.hostname.endsWith(`.${domain}`);
  } catch {
    return false;
  }
}
